import React, { Dispatch, SetStateAction } from 'react';

import { Plus4 } from '@crehana/icons';

import { PrimaryButton } from '@crehana/ui';

import { useTranslation } from '@crehana/i18n';

import PaymentsLoader from 'Jsx/CheckoutV4/shared/PaymentsLoader';
import { useCheckoutContext } from 'Jsx/CheckoutV4/Context';
import { DegreeQuota } from 'Jsx/CheckoutV4/types';
import { CustomerCard } from 'Jsx/CheckoutV4/types';
import LegalCheckbox from 'Jsx/CheckoutV4/components/LegalCheckbox';
import QuotasSelector from 'Jsx/CheckoutV4/components/QuotasSelector';

import useCustomPayment from 'Jsx/CheckoutV4/hooks/useCustomPayment';

import { PaymentSourceEnum } from 'Jsx/Types/Graphql/globals.payments';

import NewsLetterCheckbox from '../NewsLetterCheckbox';

import SavedCardsList from './SavedCardsList';

type Brand = {
  localKey: string | null;
  globalKey: string | null;
  loading: boolean;
  paymentError: string;
  paymentGateway: number | null;
  cardBrand?: string;
};

const PaymentWithSavedCard: React.FC<{
  savedCards: CustomerCard[];
  symbol: string | null;
  quotas?: DegreeQuota[];
  selectedQuotaNumber: number;
  setSelectedQuotaNumber: Dispatch<SetStateAction<number>>;
  togglePaymentWithSavedCard(): void;
}> = ({
  savedCards,
  symbol,
  quotas,
  selectedQuotaNumber,
  setSelectedQuotaNumber,
  togglePaymentWithSavedCard,
}) => {
  const { t } = useTranslation();
  const [savedCard, setSavedCard] = React.useState<Brand>({
    localKey: '',
    globalKey: '',
    loading: false,
    paymentError: '',
    paymentGateway: null,
    cardBrand: undefined,
  });

  const [checkedTerms, setCheckedTerms] = React.useState(true);
  const [checkedNewsLetterTerms, setCheckedNewsLetterTerms] =
    React.useState(false);
  const { preloadedCourseData } = useCheckoutContext();

  const { executePayment } = useCustomPayment();

  const onClickPay = () => {
    setSavedCard({ ...savedCard, loading: true });

    executePayment({
      paymentGateway: savedCard?.paymentGateway?.toString() || '',
      stripeCardId: savedCard.globalKey,
      stripeMexicoCardId: savedCard.localKey,
      paymentSource: preloadedCourseData
        ? PaymentSourceEnum.COURSE_DETAIL_POP_UP_VIEW
        : PaymentSourceEnum.CHECKOUT_VIEW,
      quotaNumber: selectedQuotaNumber,
      checkedNewsLetterTerms,
    })
      .then(({ data }) => {
        if (data?.payment?.success) {
          window.location.href = data.payment.redirectUrl || '/';
        } else {
          setSavedCard({
            ...savedCard,
            loading: false,
            paymentError:
              data?.payment?.messages?.[0]?.message || t('CATCH_MESSAGE'),
          });
        }
      })
      .catch(err => {
        setSavedCard({
          ...savedCard,
          paymentError: t('CATCH_MESSAGE'),
          loading: false,
        });
        // @ts-expect-error
        window.Sentry.withScope(scope => {
          scope.setTag('paymentMethod', 'savedCard');
          window.Sentry.captureException(err);
        });
      });
  };

  const getSelectedCard = (selectedCard: CustomerCard) => {
    if (selectedCard) {
      setSavedCard({
        ...savedCard,
        localKey: selectedCard?.cardMx || '',
        globalKey: selectedCard?.cardUsa || '',
        paymentGateway: selectedCard?.paymentGateway || 1,
        cardBrand: selectedCard?.cardBrand || '',
      });
    }
  };

  const isPaymentButtonActive = checkedTerms && !!savedCard.globalKey;

  return (
    <>
      <h2 className="font-h2 mb-24 md:hidden">{t('GO_SAVED_CARD')}</h2>
      <h3 className="font-h3 mb-24 hidden md:block">{t('GO_SAVED_CARD')}</h3>
      <div style={{ maxWidth: 399 }}>
        <div>
          <SavedCardsList
            getSelectedCard={getSelectedCard}
            savedCards={savedCards}
          />
          <button
            className="mt-16 mb-24 text-secondary-main font-link flex items-center"
            onClick={togglePaymentWithSavedCard}
            disabled={savedCard.loading}
            type="button"
          >
            <Plus4
              size={24}
              ignoreColor
              className="fill-current text-secondary-main mr-8"
            />
            <span className="normal-case">{t('ADD_SAVED_CARD')}</span>
          </button>
        </div>

        <QuotasSelector
          symbol={symbol}
          quotas={quotas}
          selectedQuotaNumber={selectedQuotaNumber}
          setSelectedQuotaNumber={setSelectedQuotaNumber}
        />

        <div
          style={{ maxWidth: 320 }}
          className="flex-grow mx-auto mt-24 sm:mx-0"
        >
          <PrimaryButton
            disabled={savedCard.loading || !isPaymentButtonActive}
            data-testid="buy-product-submit"
            onClick={onClickPay}
            full
            size="xl"
            label={t('BUY_NOW')}
            isAccent
          />
        </div>

        <LegalCheckbox
          setCheckedTerms={setCheckedTerms}
          checkedTerms={checkedTerms}
          className="mt-16"
        />
        <NewsLetterCheckbox
          checkedNewsLetterTerms={checkedNewsLetterTerms}
          setCheckedNewsLetterTerms={setCheckedNewsLetterTerms}
          className="mt-16"
        />

        {savedCard.paymentError && (
          <p className="mt-16 font-subtitle3 text-red-main">
            {savedCard.paymentError}
          </p>
        )}
        {savedCard.loading && (
          <PaymentsLoader
            type="purchaseOnline"
            cardType={savedCard.cardBrand}
          />
        )}
      </div>
    </>
  );
};

export default PaymentWithSavedCard;
